import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Play, ArrowLeft, Trash, PencilSimple, Check, X, Plus, SpeakerHigh, Share, Copy, Globe, Lock, ArrowsClockwise, Sparkle, Scissors, GraduationCap, Files, Headphones } from "@phosphor-icons/react";
import AIProgressIndicator from "../components/AIProgressIndicator";
import { toast } from "sonner";
import { speak, cancelSpeech } from "../lib/speech";
import { loadClassroomScript, renderClassroomButton } from "../lib/googleClassroom";

export default function DeckView() {
  const { deckId } = useParams();
  const [deck, setDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // card_id
  const [editContent, setEditContent] = useState("");
  const [newCard, setNewCard] = useState("");
  const [rechunking, setRechunking] = useState(false);
  const [newChunkSize, setNewChunkSize] = useState("medium");
  const [showRechunk, setShowRechunk] = useState(false);
  // Split-card modal state
  const [splittingCard, setSplittingCard] = useState(null); // card object being split
  const [splitText, setSplitText] = useState("");
  // Inline deck-title rename. Click the title to enter edit mode; Enter or
  // blur commits, Esc cancels. Lives on DeckView so owners can rename
  // without leaving the card surface.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [titleSaving, setTitleSaving] = useState(false);
  // "Append more content" panel — adds new cards to an existing deck without
  // rebuilding anything. Script decks chunk the new text at the stored
  // chunk_size; QA decks either accept pasted prompt/answer pairs or ask the
  // AI to generate N new flashcards from a topic blurb.
  const [showAppend, setShowAppend] = useState(false);
  const [appendText, setAppendText] = useState("");
  const [appendCount, setAppendCount] = useState(5);
  const [appending, setAppending] = useState(false);
  // Smart Review stats (SRS) — drives the "X due now · Y mastered" widget
  // and the Smart Review button's badge. Refreshed on load + after session.
  const [srs, setSrs] = useState(null);
  const [splitPos, setSplitPos] = useState(0);
  const [splitSaving, setSplitSaving] = useState(false);
  const splitTextareaRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [{ data }, { data: s }, srsRes] = await Promise.all([
        api.get(`/decks/${deckId}`),
        api.get(`/decks/${deckId}/stats`),
        api.get(`/reviews/stats`, { params: { deck_id: deckId } }).catch(() => ({ data: null })),
      ]);
      setDeck(data.deck);
      setCards(data.cards);
      setStats(s);
      setSrs(srsRes?.data || null);
      setNewChunkSize(data.deck.chunk_size || "medium");
    } catch (e) {
      toast.error("Could not load deck");
      navigate("/dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); return () => cancelSpeech(); }, [deckId]);

  // Load / render Google Classroom share widget when deck becomes public
  useEffect(() => {
    if (!deck?.is_public) return;
    loadClassroomScript().then(() => {
      renderClassroomButton(`gc-share-${deckId}`);
    });
  }, [deck?.is_public, deckId]);

  const saveEdit = async (cardId) => {
    try {
      const card = cards.find((c) => c.card_id === cardId);
      await api.put(`/decks/${deckId}/cards/${cardId}`, {
        content: editContent,
        prompt: card?.prompt || null,
        original: card?.original || null,
      });
      setCards((cs) => cs.map((c) => c.card_id === cardId ? { ...c, content: editContent } : c));
      setEditing(null);
      toast.success("Updated");
    } catch { toast.error("Save failed"); }
  };

  const delCard = async (cardId) => {
    console.log("[delCard] attempting", cardId);
    try {
      const res = await api.delete(`/decks/${deckId}/cards/${cardId}`);
      console.log("[delCard] success", res.status, res.data);
      setCards((cs) => cs.filter((c) => c.card_id !== cardId));
      toast.success("Card deleted");
    } catch (e) {
      console.error("[delCard] FAILED:", e);
      const msg = e?.response?.data?.detail || e?.message || "Delete failed";
      toast.error(`Delete failed: ${msg}`);
    }
  };

  const addCard = async () => {
    if (!newCard.trim()) return;
    try {
      const { data } = await api.post(`/decks/${deckId}/cards`, [{ content: newCard.trim() }]);
      setCards((cs) => [...cs, ...data.cards]);
      setNewCard("");
      toast.success("Card added");
    } catch { toast.error("Add failed"); }
  };

  const doRechunk = async () => {
    setRechunking(true);
    try {
      const { data } = await api.post(`/decks/${deckId}/rechunk`, { chunk_size: newChunkSize });
      setCards(data.cards);
      setDeck((d) => ({ ...d, chunk_size: data.chunk_size, card_count: data.card_count }));
      setShowRechunk(false);
      toast.success(`Rebuilt into ${data.card_count} ${data.chunk_size} chunks`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Rechunk failed";
      toast.error(detail, { duration: 6000 });
      console.error("Rechunk error", e);
    } finally { setRechunking(false); }
  };

  const doAppend = async () => {
    const text = appendText.trim();
    if (!text) return;
    setAppending(true);
    try {
      const body = deck.mode === "script"
        ? { text, chunk_size: deck.chunk_size || "medium" }
        : { text, count: Math.max(1, Math.min(30, appendCount || 5)) };
      const { data } = await api.post(`/decks/${deckId}/append`, body);
      const added = data.added || [];
      setCards((prev) => [...prev, ...added]);
      setDeck((d) => ({ ...d, card_count: (d.card_count || 0) + added.length }));
      toast.success(`Added ${added.length} ${added.length === 1 ? "card" : "cards"}`);
      setAppendText("");
      setShowAppend(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not append content";
      toast.error(detail, { duration: 6000 });
    } finally { setAppending(false); }
  };

  const openSplit = (card) => {
    setSplittingCard(card);
    setSplitText(card.content || "");
    // Default split: halfway through, snapped to nearest sentence end
    const text = card.content || "";
    const mid = Math.floor(text.length / 2);
    // Try to find a sentence boundary near the middle
    const candidates = [...text.matchAll(/[.!?]\s+/g)].map((m) => m.index + m[0].length);
    let best = mid;
    if (candidates.length) {
      best = candidates.reduce((a, b) => Math.abs(b - mid) < Math.abs(a - mid) ? b : a, candidates[0]);
    }
    setSplitPos(best);
    setTimeout(() => {
      const ta = splitTextareaRef.current;
      if (ta) { ta.focus(); ta.setSelectionRange(best, best); }
    }, 60);
  };

  const closeSplit = () => {
    setSplittingCard(null);
    setSplitText("");
    setSplitPos(0);
  };

  const onSplitCursor = () => {
    const ta = splitTextareaRef.current;
    if (ta) setSplitPos(ta.selectionStart ?? 0);
  };

  const doSplit = async () => {
    if (!splittingCard) return;
    const first = splitText.slice(0, splitPos).trim();
    const second = splitText.slice(splitPos).trim();
    if (!first || !second) {
      toast.error("Place the cursor between the two halves — both sides need text");
      return;
    }
    setSplitSaving(true);
    try {
      const { data } = await api.post(
        `/decks/${deckId}/cards/${splittingCard.card_id}/split`,
        { first, second },
      );
      setCards(data.cards);
      setDeck((d) => ({ ...d, card_count: data.card_count }));
      toast.success("Card split in two");
      closeSplit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
    } finally { setSplitSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[color:var(--paper)]"><Navbar/>
      <div className="max-w-5xl mx-auto py-20 px-6"><div className="wave"><span/><span/><span/><span/><span/></div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 fade-in">
        <Link to="/dashboard" className="text-sm link-underline inline-flex items-center gap-1 mb-8" data-testid="back-link">
          <ArrowLeft size={14} /> Library
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-6 border-b rule pb-8 mb-10">
          <div>
            <div className="eyebrow mb-3">
              {deck.mode === "qa"
                ? (deck.qa_style === "flashcard" ? "Flashcard deck" : "Statement deck")
                : "Script deck"} · {cards.length} cards
            </div>
            {editingTitle ? (
              <div className="flex items-baseline gap-3 flex-wrap" data-testid="deck-title-editor">
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter") {
                      const t = titleDraft.trim();
                      if (!t || t === deck.title) { setEditingTitle(false); return; }
                      setTitleSaving(true);
                      try {
                        const { data } = await api.patch(`/decks/${deckId}`, { title: t });
                        setDeck(data);
                        toast.success("Deck renamed");
                      } catch { toast.error("Could not rename deck"); }
                      setTitleSaving(false);
                      setEditingTitle(false);
                    } else if (e.key === "Escape") {
                      setEditingTitle(false);
                    }
                  }}
                  onBlur={async () => {
                    const t = titleDraft.trim();
                    if (!t || t === deck.title) { setEditingTitle(false); return; }
                    setTitleSaving(true);
                    try {
                      const { data } = await api.patch(`/decks/${deckId}`, { title: t });
                      setDeck(data);
                      toast.success("Deck renamed");
                    } catch { toast.error("Could not rename deck"); }
                    setTitleSaving(false);
                    setEditingTitle(false);
                  }}
                  disabled={titleSaving}
                  className="font-serif text-4xl sm:text-5xl leading-none tracking-tight bg-transparent border-b-2 border-[color:var(--ink)] outline-none py-1 min-w-[200px] max-w-full"
                  data-testid="deck-title-input"
                  maxLength={120}
                />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-faint)]">
                  Enter to save · Esc to cancel
                </span>
              </div>
            ) : (
              <h1
                className="font-serif text-4xl sm:text-5xl leading-none tracking-tight inline-flex items-baseline gap-3 group cursor-text"
                data-testid="deck-title"
                onClick={() => { setTitleDraft(deck.title || ""); setEditingTitle(true); }}
                title="Click to rename"
              >
                {deck.title}
                <PencilSimple
                  size={18}
                  className="text-[color:var(--ink-muted)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  data-testid="deck-title-edit-icon"
                />
              </h1>
            )}
            {deck.description && <p className="text-[color:var(--ink-muted)] mt-3 max-w-2xl">{deck.description}</p>}
            {stats && stats.attempts > 0 && (
              <div className="mt-4 flex gap-6 font-mono text-sm">
                <span><span className="eyebrow">Attempts</span> <span className="ml-2">{stats.attempts}</span></span>
                <span><span className="eyebrow">Accuracy</span> <span className="ml-2 text-[color:var(--success)]">{stats.accuracy}%</span></span>
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={async () => {
                  const next = !deck.is_public;
                  try {
                    const { data } = await api.patch(`/decks/${deckId}`, { is_public: next });
                    setDeck(data);
                    toast.success(next ? "Deck is now shareable" : "Sharing disabled");
                  } catch { toast.error("Could not update"); }
                }}
                className={`text-xs font-mono uppercase tracking-[0.2em] px-3 py-2 border rule transition-colors ${deck.is_public ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]" : "bg-white hover:border-[color:var(--ink)]"}`}
                data-testid="btn-toggle-public"
              >
                {deck.is_public ? <><Globe size={12} className="inline mr-1"/> Public</> : <><Lock size={12} className="inline mr-1"/> Private</>}
              </button>
              {deck.is_public && (
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/d/${deckId}`;
                    navigator.clipboard?.writeText(url);
                    toast.success("Link copied · " + url);
                  }}
                  className="text-xs flex items-center gap-2 px-3 py-2 border rule bg-white hover:border-[color:var(--ink)]"
                  data-testid="btn-copy-link"
                >
                  <Copy size={12}/> Copy share link
                </button>
              )}
              <button
                onClick={async () => {
                  const title = (deck.title || "Deck") + " (copy)";
                  toast(`Duplicate this deck as "${title}"?`, {
                    duration: 8000,
                    action: {
                      label: "Duplicate",
                      onClick: async () => {
                        try {
                          const { data } = await api.post(`/decks/${deckId}/clone`, {});
                          toast.success(`Duplicated · ${data.card_count} cards copied`);
                          navigate(`/decks/${data.deck_id}`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Clone failed");
                        }
                      },
                    },
                  });
                }}
                className="text-xs flex items-center gap-2 px-3 py-2 border rule bg-white hover:border-[color:var(--ink)]"
                title="Duplicate this deck (reuse across semesters)"
                data-testid="btn-clone-deck"
              >
                <Files size={12}/> Duplicate
              </button>
              {deck.is_public && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-2 border rule bg-white hover:border-[color:var(--ink)] cursor-pointer"
                  title="Share this deck to a Google Classroom"
                  data-testid="btn-share-classroom"
                >
                  <GraduationCap size={14} weight="duotone" className="text-[color:var(--oxford)]"/>
                  <span className="text-xs">Share to Google Classroom</span>
                  <div
                    id={`gc-share-${deckId}`}
                    className="g-sharetoclassroom"
                    data-size="32"
                    data-theme="classic"
                    data-url={`${window.location.origin}/d/${deckId}`}
                    data-title={deck.title || "Ekkol deck"}
                    data-body={`Practice this deck on Ekkol — ${deck.description || "voice-first memorization"}`}
                  />
                </div>
              )}
              {deck.mode === "script" && (deck.source_text || "").trim() && (
                <button
                  onClick={() => setShowRechunk((v) => !v)}
                  className="text-xs flex items-center gap-2 px-3 py-2 border rule bg-white hover:border-[color:var(--ink)]"
                  data-testid="btn-show-rechunk"
                >
                  <ArrowsClockwise size={12}/> Rebuild chunks
                </button>
              )}
              <button
                onClick={() => setShowAppend((v) => !v)}
                className="text-xs flex items-center gap-2 px-3 py-2 border rule bg-white hover:border-[color:var(--ink)]"
                data-testid="btn-show-append"
              >
                <Plus size={12}/> Append more content
              </button>
            </div>
            {showAppend && (
              <div className="mt-4 border rule bg-white p-4 fade-in" data-testid="append-panel">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                  <span className="eyebrow">
                    {deck.mode === "script" ? "Add more text — Ekkol will chunk it and append new cards" : "Add more flashcards — paste a topic or notes, AI will create N new cards"}
                  </span>
                  <button onClick={() => setShowAppend(false)} className="text-xs link-underline">Cancel</button>
                </div>
                <textarea
                  value={appendText}
                  onChange={(e) => setAppendText(e.target.value)}
                  rows={5}
                  placeholder={deck.mode === "script"
                    ? "Paste the next chapter, paragraph, or notes to add on…"
                    : "Topic, notes, or study material to generate new flashcards from…"}
                  className="w-full bg-transparent border rule px-3 py-2 font-serif text-base focus:outline-none focus:border-[color:var(--ink)]"
                  disabled={appending}
                  data-testid="append-textarea"
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {deck.mode !== "script" && (
                    <label className="flex items-center gap-2 text-xs font-mono">
                      <span className="eyebrow">How many</span>
                      <input
                        type="number"
                        min={1} max={30}
                        value={appendCount}
                        onChange={(e) => setAppendCount(parseInt(e.target.value) || 5)}
                        className="w-16 border rule px-2 py-1 bg-transparent text-center"
                        data-testid="append-count"
                      />
                      <span className="text-[color:var(--ink-faint)]">(1-30)</span>
                    </label>
                  )}
                  <button
                    onClick={doAppend}
                    disabled={appending || !appendText.trim()}
                    className="btn-primary py-2 px-4 text-sm disabled:opacity-40"
                    data-testid="btn-append-go"
                  >
                    <Sparkle size={14} weight="fill"/> {appending ? "Adding…" : "Add to deck"}
                  </button>
                  <p className="text-[11px] text-[color:var(--ink-muted)] flex-1 min-w-[220px]">
                    {deck.mode === "script"
                      ? <>Appends at the end — existing cards are untouched. Uses the deck&apos;s current chunk size (<span className="font-mono">{deck.chunk_size || "medium"}</span>).</>
                      : "Appends at the end — existing cards are untouched. AI generates prompt+answer pairs from your text."}
                  </p>
                </div>
                {appending && (
                  <div className="mt-3">
                    <AIProgressIndicator
                      title="Adding content"
                      message={deck.mode === "script" ? <>Splitting your new text into chunks…</> : <>Generating new flashcards from your notes…</>}
                      testId="append-progress"
                    />
                  </div>
                )}
              </div>
            )}
            {showRechunk && (
              <div className="mt-4 border rule bg-white p-4 flex flex-wrap items-center gap-3 fade-in">
                <span className="eyebrow">New chunk size</span>
                {["short", "medium", "long"].map((sz) => (
                  <button
                    key={sz}
                    onClick={() => setNewChunkSize(sz)}
                    className={`text-xs font-mono uppercase tracking-widest px-3 py-2 border rule ${newChunkSize === sz ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]" : "bg-white"}`}
                    data-testid={`rechunk-size-${sz}`}
                  >{sz}</button>
                ))}
                <button
                  onClick={doRechunk}
                  disabled={rechunking}
                  className="btn-primary py-2 px-4 text-sm"
                  data-testid="btn-rechunk-go"
                >
                  <Sparkle size={14} weight="fill"/> {rechunking ? "Rebuilding…" : "Rebuild now"}
                </button>
                <button onClick={() => setShowRechunk(false)} className="text-xs link-underline">Cancel</button>
                <p className="w-full text-xs text-[color:var(--ink-muted)] mt-1">
                  Replaces all current chunks with a fresh AI split at the new size. Use this to scaffold — start SHORT for first-pass memorization, move to MEDIUM, then LONG as you master the content. <span className="text-[color:var(--vermilion)]">This replaces the current {cards.length} cards.</span>
                </p>
                {rechunking && (
                  <AIProgressIndicator
                    title="Rebuilding"
                    message={<>AI is re-splitting your source text into <span className="font-mono">{newChunkSize}</span> chunks. This usually takes a few seconds.</>}
                    testId="rechunk-progress"
                  />
                )}
              </div>
            )}
          </div>
          {cards.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="deck-study-actions">
              {srs && srs.due_now > 0 && (
                <Link
                  to={`/study/${deckId}?mode=smart`}
                  className="btn-primary relative"
                  data-testid="start-smart-review"
                  title={`${srs.due_now} cards due for review`}
                >
                  <Sparkle size={16} weight="fill"/> Smart Review
                  <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[color:var(--vermilion)] text-white font-mono text-[10px]">
                    {srs.due_now}
                  </span>
                </Link>
              )}
              <Link to={`/study/${deckId}`} className={srs && srs.due_now > 0 ? "btn-ghost" : "btn-ghost"} data-testid="start-study">
                <Play size={16} weight="fill"/> Practice
              </Link>
              <Link to={`/study/${deckId}?mode=learn`} className={srs && srs.due_now > 0 ? "btn-ghost" : "btn-primary"} data-testid="start-learn">
                <Headphones size={18} weight="fill"/> Learn · listen &amp; repeat
              </Link>
            </div>
          )}
          {/* SRS stats widget — only shown once the user has reviewed at
              least one card so a brand-new deck doesn't feel overwhelming. */}
          {srs && (srs.learning + srs.reviewing + srs.mastered) > 0 && (
            <div className="mt-6 border rule bg-white p-4" data-testid="srs-stats-widget">
              <div className="eyebrow mb-3 flex items-center gap-2">
                <Sparkle size={12} weight="fill" className="text-[color:var(--oxford)]"/>
                Smart Review · progress
              </div>
              <div className="grid grid-cols-4 gap-px bg-[color:var(--rule)] border rule">
                <div className="bg-white p-3 text-center" data-testid="srs-new">
                  <div className="text-[10px] eyebrow">New</div>
                  <div className="font-mono text-xl mt-1">{srs.new}</div>
                </div>
                <div className="bg-white p-3 text-center" data-testid="srs-learning">
                  <div className="text-[10px] eyebrow text-[color:var(--oxford)]">Learning</div>
                  <div className="font-mono text-xl mt-1 text-[color:var(--oxford)]">{srs.learning}</div>
                </div>
                <div className="bg-white p-3 text-center" data-testid="srs-reviewing">
                  <div className="text-[10px] eyebrow">Reviewing</div>
                  <div className="font-mono text-xl mt-1">{srs.reviewing}</div>
                </div>
                <div className="bg-white p-3 text-center" data-testid="srs-mastered">
                  <div className="text-[10px] eyebrow text-[color:var(--success)]">Mastered</div>
                  <div className="font-mono text-xl mt-1 text-[color:var(--success)]">{srs.mastered}</div>
                </div>
              </div>
              {srs.due_now > 0 ? (
                <p className="mt-3 text-xs text-[color:var(--ink-muted)]">
                  <strong className="text-[color:var(--vermilion)] font-mono">{srs.due_now}</strong> {srs.due_now === 1 ? "card" : "cards"} ready for review right now. Smart Review shuffles in due cards first, then new ones.
                </p>
              ) : srs.due_today > 0 ? (
                <p className="mt-3 text-xs text-[color:var(--ink-muted)]">
                  <strong className="font-mono">{srs.due_today}</strong> {srs.due_today === 1 ? "card comes" : "cards come"} due later today.
                </p>
              ) : (
                <p className="mt-3 text-xs text-[color:var(--ink-muted)]">
                  All caught up. Come back when cards are due — we&apos;ll surface them in the Smart Review button above.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="border rule bg-white divide-y rule" data-testid="cards-list">
          {cards.map((c, i) => (
            <div key={c.card_id} className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-5 sm:items-start group" data-testid={`card-row-${i}`}>
              <div className="flex gap-4 items-start flex-1 min-w-0">
                <div className="font-mono text-xs pt-2 w-8 text-[color:var(--ink-faint)] shrink-0">{String(i+1).padStart(2,"0")}</div>
                <div className="flex-1 min-w-0">
                  {editing === c.card_id ? (
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={2}
                      className="w-full bg-transparent border rule px-3 py-2 font-serif text-lg focus:outline-none focus:border-[color:var(--ink)]"
                      autoFocus
                    />
                  ) : c.prompt ? (
                    <>
                      <p className="font-serif text-lg leading-relaxed break-words">{c.prompt}</p>
                      <p className="mt-1 font-serif text-base text-[color:var(--ink-muted)] break-words">
                        <span className="eyebrow mr-2">Answer</span>{c.content}
                      </p>
                    </>
                  ) : (
                    <p className="font-serif text-lg leading-relaxed break-words">{c.content}</p>
                  )}
                  {c.original && editing !== c.card_id && (
                    <p className="mt-2 font-mono text-xs text-[color:var(--ink-faint)] whitespace-pre-wrap break-words">{c.original}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 justify-end sm:justify-start flex-wrap sm:flex-nowrap shrink-0 pl-12 sm:pl-0 border-t rule sm:border-0 pt-3 sm:pt-0 w-full sm:w-auto">
                {editing === c.card_id ? (
                  <>
                    <button onClick={() => saveEdit(c.card_id)} className="text-[color:var(--success)] p-1" data-testid={`save-card-${i}`}><Check size={18}/></button>
                    <button onClick={() => setEditing(null)} className="text-[color:var(--ink-faint)] p-1"><X size={18}/></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { cancelSpeech(); speak(c.prompt || c.content); }} className="hover:text-[color:var(--oxford)] p-2" aria-label="Preview voice" title="Preview voice" data-testid={`play-card-${i}`}>
                      <SpeakerHigh size={18}/>
                    </button>
                    <button onClick={() => { setEditing(c.card_id); setEditContent(c.content); }} className="hover:text-[color:var(--ink)] p-2" aria-label="Edit card" title="Edit card" data-testid={`edit-card-${i}`}>
                      <PencilSimple size={16}/>
                    </button>
                    {!c.prompt && (c.content || "").length > 40 && (
                      <button
                        onClick={() => openSplit(c)}
                        className="hover:text-[color:var(--oxford)] p-2"
                        aria-label="Split card"
                        title="Split this card into two"
                        data-testid={`split-card-${i}`}
                      >
                        <Scissors size={16}/>
                      </button>
                    )}
                    <button
                      onClick={() => delCard(c.card_id)}
                      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[color:var(--vermilion)] hover:text-white hover:bg-[color:var(--vermilion)] border border-[color:var(--vermilion)]/50 hover:border-[color:var(--vermilion)] rounded px-2 sm:px-3 py-2 transition-colors"
                      aria-label="Delete card"
                      title="Delete card"
                      data-testid={`delete-card-${i}`}
                    >
                      <Trash size={14} weight="bold"/> <span className="hidden sm:inline">Remove</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          <div className="p-4 sm:p-5 flex gap-3 sm:gap-5 items-start">
            <div className="font-mono text-xs pt-3 w-8 text-[color:var(--ink-faint)] shrink-0">+</div>
            <input
              value={newCard}
              onChange={(e) => setNewCard(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCard()}
              placeholder="Add another card…"
              className="flex-1 min-w-0 bg-transparent border-b rule px-2 py-2 focus:outline-none focus:border-[color:var(--ink)]"
              data-testid="input-new-card"
            />
            <button onClick={addCard} className="btn-ghost py-2 px-3 sm:px-4 text-sm shrink-0" data-testid="btn-add-card">
              <Plus size={14}/> <span className="hidden sm:inline">Add</span>
            </button>
          </div>
        </div>

        {/* Split-card modal */}
        {splittingCard && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 fade-in"
            data-testid="split-modal"
            onClick={(e) => { if (e.target === e.currentTarget) closeSplit(); }}
          >
            <div className="bg-white border rule w-full max-w-2xl max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between border-b rule px-6 py-4">
                <div className="flex items-center gap-2">
                  <Scissors size={18} weight="duotone"/>
                  <div>
                    <div className="eyebrow">Split card</div>
                    <h2 className="font-serif text-xl leading-none mt-1">Divide this chunk into two</h2>
                  </div>
                </div>
                <button onClick={closeSplit} className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]" aria-label="Close">
                  <X size={20}/>
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-[color:var(--ink-muted)]">
                  Place your cursor where the card should break, then click <span className="font-mono">Split here</span>. The first half stays in this card; the second half becomes the next card.
                </p>
                <textarea
                  ref={splitTextareaRef}
                  value={splitText}
                  onChange={(e) => setSplitText(e.target.value)}
                  onKeyUp={onSplitCursor}
                  onClick={onSplitCursor}
                  onSelect={onSplitCursor}
                  rows={8}
                  className="w-full bg-[color:var(--paper)] border rule px-4 py-3 font-serif text-lg leading-relaxed focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="split-textarea"
                />
                <div className="grid sm:grid-cols-2 gap-px bg-[color:var(--rule)] border rule">
                  <div className="bg-white p-4">
                    <div className="eyebrow mb-2">Card 1 (current)</div>
                    <p className="font-serif text-sm leading-relaxed min-h-[3rem]" data-testid="split-preview-first">
                      {splitText.slice(0, splitPos).trim() || <span className="text-[color:var(--ink-faint)] italic">— empty —</span>}
                    </p>
                  </div>
                  <div className="bg-white p-4">
                    <div className="eyebrow mb-2">Card 2 (new)</div>
                    <p className="font-serif text-sm leading-relaxed min-h-[3rem]" data-testid="split-preview-second">
                      {splitText.slice(splitPos).trim() || <span className="text-[color:var(--ink-faint)] italic">— empty —</span>}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-[color:var(--ink-faint)] font-mono">
                  Cursor at character {splitPos} of {splitText.length}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 border-t rule px-6 py-4">
                <button onClick={closeSplit} className="btn-ghost" data-testid="btn-split-cancel">Cancel</button>
                <button
                  onClick={doSplit}
                  disabled={splitSaving || !splitText.slice(0, splitPos).trim() || !splitText.slice(splitPos).trim()}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="btn-split-confirm"
                >
                  <Scissors size={14} weight="fill"/> {splitSaving ? "Splitting…" : "Split here"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
